import { Component, Input, ViewChild, ElementRef, HostListener, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { NgbDatepickerCustomI18n } from 'src/app/modules/forms/components/input-date/NgbDatepickerCustomI18n';
import { InputComponent } from '../input/input.component';
import {
	NgbDateParserFormatter,
	NgbInputDatepicker,
	NgbDatepickerConfig,
	NgbDateStruct,
	NgbDatepickerI18n
} from '@ng-bootstrap/ng-bootstrap';
import { NgbDateFnsDateParserFormatter } from './ngb-datefns-date-parser-formatter';
import { FormControl } from '@angular/forms';
import * as _ from 'lodash';

/**
 * Input Date component to show input for a Date.
 *
 * @example
 * <cfe-input-date
 *                 [id]="'date'"
 *                 [control]="formControl"
 *                 [label]="'Datum'"
 *                 [placeholder]=""
 *                 [disabled]="false"
 *                 [notMarkAsRequired]="false"
 *                 [tabIndex]="1"
 *                 [minDate]="minDate"
 *                 [maxDate]="maxDate">
 * </cfe-input-date>
 */

@Component({
	selector: 'cfe-input-date',
	templateUrl: './input-date.component.html',
	styleUrls: ['./input-date.component.scss'],
	providers: [
		{ provide: NgbDatepickerI18n, useClass: NgbDatepickerCustomI18n },
		{ provide: NgbDateParserFormatter, useClass: NgbDateFnsDateParserFormatter },
		NgbDatepickerConfig
	]
})
export class InputDateComponent extends InputComponent implements AfterViewInit {
	/**
	 * Placeholder text
	 */
	@Input() placeholder = '';

	/**
	 * Minimals Datum
	 */
	@Input() minDate: Date;

	/**
	 * Maximales Datum
	 */
	@Input() maxDate: Date;

	/**
	 * ngb date picker
	 */
	@ViewChild('datePicker', { static: true }) datePicker: NgbInputDatepicker;

	/**
	 * Subscription
	 */
	protected subscription: Subscription;

	/**
	 * Konstruktor
	 */
	constructor(private elementRef: ElementRef, private config: NgbDatepickerConfig, protected translate: TranslateService) {
		super();
	}

	/**
	 * OnInit function for overwriting to extend ngOnInit
	 */
	cfeOnInit() {
		// Maximales Datum setzen
		if (this.minDate) {
			this.config.minDate = { day: this.minDate.getDate(), month: this.minDate.getMonth() + 1, year: this.minDate.getFullYear() };
		}

		// Minimales Datum setzen
		if (this.maxDate) {
			this.config.maxDate = { day: this.maxDate.getDate(), month: this.maxDate.getMonth() + 1, year: this.maxDate.getFullYear() };
		}
	}

	/**
	 * Datepicker schliessen wenn ausserhalb der aktuellen Komponente geklicked wird.
	 *
	 * @param event Event
	 */
	@HostListener('document:click', ['$event'])
	onHostclick(event) {
		if (this.datePicker.isOpen()) {
			const dpNavigationSelect: HTMLCollectionOf<Element> = document.getElementsByClassName('ngb-dp-navigation-select');
			dpNavigationSelect[0].childNodes.forEach((childNode) => {
				if (childNode['title'] === 'Select month') {
					childNode['title'] = this.translate.instant('inputDate.navigation.selectMonth');
				}
				if (childNode['title'] === 'Select year') {
					childNode['title'] = this.translate.instant('inputDate.navigation.selectYear');
				}
			});

			const dpArrowButton: HTMLCollectionOf<Element> = document.getElementsByClassName('btn btn-link ngb-dp-arrow-btn');
			_.forEach(dpArrowButton, (element) => {
				if (element['title'] === 'Previous month') {
					element['title'] = this.translate.instant('inputDate.navigation.previousMonth');
				}
				if (element['title'] === 'Next month') {
					element['title'] = this.translate.instant('inputDate.navigation.nextMonth');
				}
			});
		}

		// Wurde ausserhalb der aktuellen Komponente geklickt
		if (!this.elementRef.nativeElement.contains(event.target)) {
			// dann den DatePicker schliessen, wenn er offen ist
			if (this.datePicker.isOpen()) {
				this.datePicker.close();
			}
		}
	}

	/**
	 * ngOnChanges
	 *
	 * @param changes
	 */
	cfeOnChanges(changes) {
		if (changes['placeholder'] || changes['label']) {
			// Check for Language switch
			if (this.control.value) {
				this.datePicker.manualDateChange(this.control.value, true);
			}
		}
	}

	/**
	 * Angular lifecyle hook ngAfterViewInit
	 * Da der NgbDatepicker aktuell auf die NgbDateStruct arbeitet,
	 * werde hier einige Methoden umgeschrieben.
	 * Siehe dazu: https://ng-bootstrap.github.io/#/components/datepicker/overview#date-model
	 */
	ngAfterViewInit(): void {
		const dp = <any>this.datePicker;
		const _onChange = dp._onChange;

		// Konvertierung von NgbDateStruct => Date
		dp._onChange = (value: NgbDateStruct) => {
			let date: any = value;
			if (value != null) {
				// NgbDateStruct in ein Date umwandel
				date = new Date(Date.UTC(value.year, value.month - 1, value.day));
			}
			_onChange(date);
		};

		// Date validieren
		dp.validate = (control: FormControl) => {
			if (control.value === null || control.value === undefined) {
				return null;
			}
			const value: Date = new Date(control.value);

			if (!value.getTime()) {
				return { invalid: true };
			}

			if (this.minDate && value < this.minDate) {
				return { requiredBefore: this.minDate };
			}

			if (this.maxDate && value > this.maxDate) {
				return { requiredAfter: this.maxDate };
			}
		};
	}
}
